/**
 * One row per client: who they are and how far they have come, joined from the
 * directory (access, fee) and the operations summary (Bybit, trading, money).
 *
 * The two answers come from different reads; a client the operations summary
 * does not list yet is a client who has not got that far, never a zero.
 */
import type { ClientOperationsResponse, PlatformClientResponse } from "@/shared/api";
import { atLeast, atMost, compareDecimal, money, percent, sumDecimal } from "@/shared/money/money";

/** The path every client walks, in order. */
export const STAGES = ["Приглашение", "Вход", "Bybit", "Торговля"] as const;

/** An account proven this long ago is still being watched normally. */
export const PROOF_FRESH_SECONDS = 300;

/** Bybit liquidates the account at a margin load of 1; these warn well before. */
export const MARGIN_WARNING = "0.5";
export const MARGIN_DANGER = "0.8";
/** An open position this far below its average entry is named. */
export const POSITION_WARNING = "-0.4";

export type Attention = { readonly tone: "danger" | "warning"; readonly text: string };

export type ClientRow = {
  readonly key: string;
  readonly client: PlatformClientResponse;
  readonly operations: ClientOperationsResponse | undefined;
  /** How many of STAGES are done: 1 invited … 4 trading; 0 without access. */
  readonly reached: number;
  /**
   * The administrator's own trading account: it is a scope of the admin, not
   * a client in the directory, so only the operations summary lists it. It
   * pays no platform fee and is opened from the header, not "as a client".
   */
  readonly own: boolean;
  readonly next: string;
  readonly attention: readonly Attention[];
};

function reachedOf(client: PlatformClientResponse, operations: ClientOperationsResponse | undefined): number {
  if (client.access === "NO_ACCESS") return 0;
  if (client.access !== "ACTIVE") return 1;
  if (operations?.bybit_connected !== true) return 2;
  return operations.trading ? 4 : 3;
}

const NEXT: Readonly<Record<number, string>> = {
  0: "Доступа нет",
  1: "Ждём, когда клиент примет приглашение",
  2: "Ждём ключ Bybit от клиента",
  3: "Ключ подключён — клиент ещё не включил торговлю",
  4: "Торгует",
};

function attentionOf(client: PlatformClientResponse, operations: ClientOperationsResponse | undefined): Attention[] {
  const found: Attention[] = [];
  if (client.access === "INVITATION_EXPIRED") {
    found.push({ tone: "warning", text: "Приглашение истекло — создай клиента заново" });
  }
  if (operations === undefined) return found;
  if (operations.bybit_connected) {
    const age = operations.proof_age_seconds;
    if (age === null) found.push({ tone: "danger", text: "Связь с биржей не подтверждена" });
    else if (age > PROOF_FRESH_SECONDS) {
      found.push({ tone: "danger", text: `Нет связи с биржей ${String(Math.floor(age / 60))} мин` });
    }
  }
  const load = operations.margin_load;
  if (load != null && atLeast(load, MARGIN_DANGER)) {
    found.push({ tone: "danger", text: `Риск ликвидации: нагрузка на маржу ${percent(load, 0)}` });
  } else if (load != null && atLeast(load, MARGIN_WARNING)) {
    found.push({ tone: "warning", text: `Высокая нагрузка на маржу: ${percent(load, 0)}` });
  }
  const worst = operations.worst_position;
  if (worst != null && atMost(worst, POSITION_WARNING)) {
    found.push({ tone: "warning", text: `Позиция в просадке ${percent(worst, 0)} от средней цены входа` });
  }
  const balance = operations.fee_balance;
  // Only once the account can trade: before its key a zero balance stops
  // nothing, and a newly invited client read as "fees exhausted" (2026-10-13).
  if (operations.bybit_connected && balance != null && operations.fee_exhausted) {
    found.push({ tone: "warning", text: "Долг по комиссии достиг лимита — бот не открывает новые сделки" });
  } else if (operations.bybit_connected && balance != null && operations.fee_low) {
    found.push({ tone: "warning", text: `Долг по комиссии у лимита: баланс ${money(balance)} USDT` });
  }
  return found;
}

/**
 * `operations` undefined: the summary has not answered (or failed). An active
 * client then reads "unknown", never "waiting for a key" it may well have.
 */
export function roster(
  clients: readonly PlatformClientResponse[],
  operations: readonly ClientOperationsResponse[] | undefined,
  /** "tenant/account" of the administrator's own accounts, from its scopes. */
  ownKeys: ReadonlySet<string> = new Set(),
): ClientRow[] {
  const byKey = new Map((operations ?? []).map((item) => [`${item.tenant_id}/${item.account_id}`, item]));
  const rows = clients.map((client): ClientRow => {
    const key = `${client.tenant_id}/${client.account_id}`;
    const item = byKey.get(key);
    const reached = reachedOf(client, item);
    const unknown = operations === undefined && client.access === "ACTIVE";
    const next = unknown ? "Состояние счёта неизвестно" : NEXT[reached] ?? "";
    return { key, client, operations: item, reached, next, own: false, attention: attentionOf(client, item) };
  });
  const listed = new Set(rows.map((row) => row.key));
  // An account only the summary lists is the administrator's own when its
  // scopes say so; any other is a client the directory has not caught up
  // with (the two refresh apart), shown as such rather than as "mine, 0 %".
  const extra = (operations ?? [])
    .filter((item) => !listed.has(`${item.tenant_id}/${item.account_id}`))
    .map((item): ClientRow => {
      const key = `${item.tenant_id}/${item.account_id}`;
      const own = ownKeys.has(key);
      const client: PlatformClientResponse = {
        tenant_id: item.tenant_id,
        account_id: item.account_id,
        display_name: item.display_name,
        owner_login: null,
        access: "ACTIVE",
        created_at: "",
      };
      const reached = reachedOf(client, item);
      const next = own ? NEXT[reached] ?? "" : "Нет в списке клиентов — обнови страницу";
      return { key, client, operations: item, reached, next, own, attention: attentionOf(client, item) };
    });
  return [...extra.filter((row) => row.own), ...rows, ...extra.filter((row) => !row.own)];
}

/**
 * The period's closed-cycle profit over accounts whose analytics answered. An
 * account without one is left out and named as left out, so the total never
 * reads as exact. Deposits and withdrawals are never in it.
 */
export function periodProfit(accounts: readonly ClientOperationsResponse[]): { readonly total: string; readonly pending: number } {
  const counted = accounts.flatMap((item) => (item.period == null ? [] : [item.period.realised]));
  return { total: sumDecimal(counted), pending: accounts.length - counted.length };
}

/**
 * What the platform has earned, and what it has not. One reading for every
 * screen that shows it.
 *
 * A fee covered by a client's welcome bonus is no money to the platform, and a
 * fee neither the bonus nor the deposits covered is owed, not received (owner,
 * 2026-10-15). Обзор once summed every accrued fee under "Комиссия платформы"
 * while clients were still trading on their bonus, and showed income that had
 * not been earned (owner, 2026-10-16).
 */
export function platformIncome(accounts: readonly ClientOperationsResponse[]): {
  readonly paid: string;
  readonly bonus: string;
  readonly owed: string;
} {
  const each = (pick: (item: ClientOperationsResponse) => string | null | undefined): string =>
    sumDecimal(accounts.flatMap((item) => {
      const value = pick(item);
      return value == null ? [] : [value];
    }));
  return {
    paid: each((item) => item.fee_paid),
    bonus: each((item) => item.fee_bonus_spent),
    owed: each((item) => item.fee_owed),
  };
}

/** The income line's explanation, worded once. */
export function incomeNote(income: ReturnType<typeof platformIncome>): string {
  return `оплачено клиентами; покрыто бонусами ${money(income.bonus)}; долг клиентов ${money(income.owed)}`;
}

/** What the client table is ranked by (owner, 2026-10-29: a rating of accounts). */
export type RankBy = "rate" | "realised" | "equity";

export const RANK_LABELS: Readonly<Record<RankBy, string>> = {
  rate: "доходность в мес.",
  realised: "прибыль",
  equity: "капитал",
};

function rankValue(row: ClientRow, by: RankBy): string | null {
  const item = row.operations;
  if (item === undefined) return null;
  if (by === "equity") return item.equity ?? null;
  if (by === "realised") return item.period?.realised ?? null;
  // A month carried from the days each account traded, so an account a week
  // old and one a month old are compared on the same length (owner,
  // 2026-10-29). Under three days traded there is none, and it goes unranked.
  return item.period?.monthly ?? null;
}

/**
 * Best first by the chosen figure; an account that does not know it goes last,
 * unranked, rather than read as zero. Ties keep the directory's order.
 */
export function ranked(rows: readonly ClientRow[], by: RankBy): { readonly row: ClientRow; readonly place: number | null }[] {
  const known = rows.flatMap((row, index) => {
    const value = rankValue(row, by);
    return value === null ? [] : [{ row, index, value }];
  });
  known.sort((left, right) => compareDecimal(right.value, left.value) || left.index - right.index);
  const unknown = rows.filter((row) => rankValue(row, by) === null);
  return [
    ...known.map((entry, index) => ({ row: entry.row, place: index + 1 })),
    ...unknown.map((row) => ({ row, place: null })),
  ];
}
